/**
 * ============================================================================
 * Subscription Management API Client
 * ============================================================================
 */

import apiClient from './client';

export interface SubscriptionItem {
    id: number;
    subscription_id: number;
    day_of_week: number;
    product_id: number;
    product_name: string;
    quantity: number;
    unit_price: number;
    delivery_date?: string;
}

export interface Address {
    first_name?: string;
    last_name?: string;
    company?: string;
    address_1?: string;
    address_2?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
    email?: string;
    phone?: string;
}

export interface SubscriptionItemCreate {
    day_of_week?: number;
    delivery_date?: string;
    product_id: number;
    quantity: number;
    unit_price?: number;
}

export interface Subscription {
    id: number;
    customer_id: number;
    customer_name: string;
    customer_email: string;
    customer_phone: string;
    status: 'active' | 'paused' | 'cancelled';
    discount_type: 'flat' | 'percentage';
    discount_value: number;
    notes?: string;
    created_at: string;
    last_modified_by: string;
    items: SubscriptionItem[];

    // Address & Shipping
    billing_address?: Address;
    shipping_address?: Address;
    shipping_method_title?: string;
    shipping_total?: number;

    // Delivery Slots
    delivery_slot_id?: string;
    delivery_slot_display?: string;
}

export interface SubscriptionCreate {
    customer_id: number;
    discount_type: 'flat' | 'percentage';
    discount_value: number;
    notes?: string;
    items: SubscriptionItemCreate[];

    // Address & Shipping
    billing_address?: Address;
    shipping_address?: Address;
    shipping_method_title?: string;
    shipping_total?: number;

    // Slots
    delivery_slot_id?: string;
    delivery_slot_display?: string;
}

export interface SubscriptionUpdate {
    status?: 'active' | 'paused' | 'cancelled';
    discount_type?: 'flat' | 'percentage';
    discount_value?: number;
    notes?: string;
    billing_address?: Address;
    shipping_address?: Address;
    shipping_method_title?: string;
    shipping_total?: number;
    delivery_slot_id?: string;
    delivery_slot_display?: string;
}

export interface DeliveryStats {
    delivery_count: number;
    item_summary: Record<string, number>;
    total_revenue: number;
}

export interface SubscriptionDashboardStats {
    today: DeliveryStats;
    tomorrow: DeliveryStats;
    day_after: DeliveryStats;
}

export interface SubscriptionPreviewRequest {
    customer_id?: number;
    items: SubscriptionItemCreate[];
    discount_type: 'flat' | 'percentage';
    discount_value: number;
    shipping_total?: number;
}

export interface SubscriptionPreviewResponse {
    items: {
        product_id: number;
        product_name: string;
        quantity: number;
        unit_price: number;
        line_total: number;
    }[];
    subtotal: number;
    discount_amount: number;
    total: number;
    tax_inclusive: boolean;
}

/**
 * Get dashboard stats
 */
export const getSubscriptionStats = async (): Promise<SubscriptionDashboardStats> => {
    const response = await apiClient.get<SubscriptionDashboardStats>('/subscriptions/stats');
    return response.data;
};

/**
 * List all subscriptions
 */
export const listSubscriptions = async (): Promise<Subscription[]> => {
    const response = await apiClient.get<Subscription[]>('/subscriptions/');
    return response.data;
};

/**
 * Get specific subscription
 */
export const getSubscription = async (id: number): Promise<Subscription> => {
    const response = await apiClient.get<Subscription>(`/subscriptions/${id}`);
    return response.data;
};

/**
 * Create new subscription
 */
export const createSubscription = async (data: SubscriptionCreate): Promise<Subscription> => {
    const response = await apiClient.post<Subscription>('/subscriptions/', data);
    return response.data;
};

/**
 * Update subscription metadata
 */
export const updateSubscription = async (id: number, data: SubscriptionUpdate): Promise<Subscription> => {
    const response = await apiClient.patch<Subscription>(`/subscriptions/${id}`, data);
    return response.data;
};

/**
 * Replace subscription items
 */
export const replaceSubscriptionItems = async (id: number, items: SubscriptionItemCreate[]): Promise<Subscription> => {
    const response = await apiClient.put<Subscription>(`/subscriptions/${id}/items`, items);
    return response.data;
};

/**
 * Preview subscription totals
 */
export const previewSubscription = async (data: SubscriptionPreviewRequest): Promise<SubscriptionPreviewResponse> => {
    const response = await apiClient.post<SubscriptionPreviewResponse>('/subscriptions/preview', data);
    return response.data;
};

/**
 * Download subscription PDF
 */
export const downloadSubscriptionPDF = async (id: number, customerName: string): Promise<void> => {
    const response = await apiClient.get(`/subscriptions/${id}/pdf`, {
        responseType: 'blob'
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Subscription_Schedule_${customerName.replace(/\s+/g, '_')}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};
/**
 * Delete a subscription permanently
 */
export const deleteSubscription = async (id: number): Promise<void> => {
    await apiClient.delete(`/subscriptions/${id}`);
};

/**
 * Manually push a subscription to WooCommerce
 */
export const pushToWooCommerce = async (id: number, deliveryDate: string): Promise<any> => {
    const response = await apiClient.post(`/subscriptions/${id}/push-to-woo`, null, {
        params: { delivery_date: deliveryDate }
    });
    return response.data;
};

/**
 * Manually push ALL active subscriptions for a date to WooCommerce
 */
export const bulkPushToWooCommerce = async (deliveryDate: string): Promise<any> => {
    const response = await apiClient.post(`/subscriptions/bulk-push`, null, {
        params: { delivery_date: deliveryDate }
    });
    return response.data;
};

/**
 * Get available delivery slots
 */
export const getDeliverySlots = async (pincode: string, date?: string): Promise<any[]> => {
    const response = await apiClient.get<any[]>('/subscriptions/delivery-slots', {
        params: { pincode, delivery_date: date }
    });
    return response.data;
};
